<template>
  <div ref="warpRef" :id="chartData._id" class="jzliu-bi-engine-chart-warp" :style="{ height: warpHeight }"></div>
</template>

<script>
import { Pie } from '@antv/g2plot'
import { getCategoryDimension, initHeight } from '../../utils/index'
import { customKey } from '../../utils/enum.data'
import { hexToRgba } from '../../utils/index'
import { elResizeObverse } from '../../utils'
import { getSeriesFieldValues } from '../../utils/utils'

export default {
  name: 'column',
  props: {
    chartData: {
      type: Object,
      default: () => {
        return {}
      },
    },
    chartConfigHandler: {
      type: Function,
      default: (conf) => conf,
    },
  },
  data() {
    return {
      isLoad: false,
      warpHeight: 200,
      renderTimer: null,
      piePlot: null,
    }
  },
  watch: {
    'chartData.styleConfig.helperData.fieldMap': {
      handler() {
        this.debounceRenderChart()
      },
      deep: true
    },
    'chartData.styleConfig.commongConfig.labelConfig': {
      handler() {
        this.debounceRenderChart()
      },
      deep: true
    },
    'chartData.styleConfig.commongConfig.legendConfig': {
      handler() {
        this.debounceRenderChart()
      },
      deep: true
    },
    'chartData.styleConfig.commongConfig.columnBackgroundOpacity': {
      handler() {
        this.debounceRenderChart()
      },
      deep: true
    },
  },
  created() {
    if (!this.chartData.styleConfig.commongConfig.labelConfig?.pieContent?.length) {
      this.$set(this.chartData.styleConfig.commongConfig.labelConfig, 'pieContent', [])
    }
  },
  mounted() {
    this.isLoad = false
    const el = this.$refs.warpRef
    elResizeObverse(el, () => {
      if (this.isLoad && this.piePlot) {
        this.piePlot.render()
      }
    })
    this.$nextTick(() => {
      initHeight.call(this, 'warpHeight')
      this.renderChart()
      this.isLoad = true
    })
  },
  methods: {
    // 防抖渲染图表
    debounceRenderChart() {
      if (this.renderTimer) {
        clearTimeout(this.renderTimer)
      }
      this.renderTimer = setTimeout(() => {
        this.piePlot && this.piePlot.destroy()
        this.renderChart()
      }, 300)
    },

    /**
     * 根据维度字段聚合数据
     * @param {Array} data - 原始数据数组
     * @param {String} dimensionField - 维度字段名（如 'behavior_type'）
     * @param {String} measureField - 度量字段名（如 'click_count'）
     * @returns {Array} 聚合后的数据数组
     * 
     * 示例：
     * 原始数据：
     * [
     *   { behavior_type: '浏览', click_count: 320, user_level: '普通' },
     *   { behavior_type: '点击', click_count: 450, user_level: 'VIP' },
     *   { behavior_type: '浏览', click_count: 200, user_level: '高级' }
     * ]
     * 
     * 聚合后（按 behavior_type 聚合 click_count）：
     * [
     *   { behavior_type: '浏览', click_count: 520, user_level: '高级' },
     *   { behavior_type: '点击', click_count: 450, user_level: 'VIP' }
     * ]
     */
    aggregateDataByDimension(data, dimensionField, measureField) {
      if (!data || !Array.isArray(data) || !dimensionField || !measureField) {
        return data || []
      }

      // 使用 Map 来聚合相同维度值的数据
      const aggregatedMap = new Map()

      data.forEach(item => {
        const dimensionValue = item[dimensionField]
        const measureValue = parseFloat(item[measureField]) || 0

        if (aggregatedMap.has(dimensionValue)) {
          // 如果已存在该维度值，累加度量值
          const existingItem = aggregatedMap.get(dimensionValue)
          existingItem[measureField] += measureValue

          // 保留其他字段的最新值（可选，根据业务需求调整）
          Object.keys(item).forEach(key => {
            if (key !== dimensionField && key !== measureField) {
              existingItem[key] = item[key]
            }
          })
        } else {
          // 如果不存在该维度值，创建新记录
          const newItem = { ...item }
          newItem[measureField] = measureValue
          aggregatedMap.set(dimensionValue, newItem)
        }
      })

      // 将 Map 转换为数组
      return Array.from(aggregatedMap.values())
    },
    isValidTimestamp(value) {
      if (typeof value !== 'number') return false;
      const date = new Date(value);
      return !isNaN(date.getTime());
    },
    renderChart() {
      // 饼图不存在行列的概念 所以维度只存在于rows中 有且只有1个维度
      // 色块区域取度量第一个 其他以tooltip展示
      const {
        _id: id,
        data: {
          data: chartData,
          meta,
        },
      } = this.chartData
      const { rows } = getCategoryDimension(this.chartData.fields)
      const columns = (this.chartData.fields.find((item) => item.alias === 'measure')?.chooseFields ?? []).map((item) => item.unitCode)
      const labelConfig = this.chartData.styleConfig.commongConfig.labelConfig
      const legendConfig = this.chartData.styleConfig.commongConfig.legendConfig
      const pieContent = this.chartData.styleConfig.commongConfig.labelConfig.pieContent

      // 根据rows[0]维度字段对数据进行聚合
      const aggregatedData = this.aggregateDataByDimension(chartData, rows[0], columns[0])

      const colorField = rows[0] || ''
      const conf = {
        // appendPadding: 10,
        padding: 0,
        angleField: columns[0] || '',
        colorField,
        radius: 0.8,
        meta: {
          [colorField]: {
            type: 'cat',
            formatter: v => {
              return String(v)
            }
          }
        },
        // tooltip: {
        //   fields: [...columns, rows[0]],
        //   customContent: (title, data) => toolTipFormatter(data, meta, rows[0]),
        // },
        color: (d) => {
          const baseColor = this.chartData.styleConfig.helperData.fieldMap[d[colorField]]?.[customKey + 'seriesColor']
          return hexToRgba(baseColor, this.chartData.styleConfig.commongConfig.columnBackgroundOpacity / 100)
        },
        legend: legendConfig.show ? {
          itemHeight: 18,
          position: legendConfig.position,
          offsetY: 2,
          marker: (text, index, item) => {
            return {
              symbol: 'circle', // 使用圆形标记
              style: {
                fill: this.chartData.styleConfig.helperData.fieldMap[item[colorField]]?.[customKey + 'seriesColor'],
              },
            };
          },
          itemName: {
            style: {
              fill: '#4E5969', // 图例文字颜色
              fontSize: 12, // 字体大小
              lineHeight: 20, // 行高+8 (12 + 8 = 20)
            }
          }
        } : false,
        label: labelConfig.show ? {
          type: labelConfig.position,
          content: (info) => {
            let text = ''
            const { percent } = info
            if (pieContent.includes('dim')) {
              text += info[colorField] + ' '
            }
            if (pieContent.includes('measure')) {
              text += info[columns[0]] + ' '
            }
            if (pieContent.includes('percent')) {
              text += `(${(info.percent * 100).toFixed(labelConfig.textConfig.decimalPlaces)}%)`
            }
            return text
          },
          autoHide: !labelConfig.showAll,
          style: {
            fontSize: labelConfig.textConfig.fontSize,
            fill: labelConfig.textConfig.color,
            fontWeight: labelConfig.textConfig.isBold ? 'bold' : 'normal',
            fontStyle: labelConfig.textConfig.isItalic ? 'italic' : 'normal',
          }
        } : false,
      }
      console.log(aggregatedData, this.chartData, '====')
      const confOpt = this.chartData.isCancel ? conf : this.chartConfigHandler(conf, this.chartData)
      this.piePlot = new Pie(id, {
        ...confOpt,
        data: aggregatedData,
        meta: {
          ...getSeriesFieldValues(this.chartData, colorField, colorField)
        },
      })
      this.piePlot.render()
      this.piePlot.on('element:click', (res) => {
        this.$emit('drill-item-click', res.data)
      });
    },
    destroyedChart() {
      this.piePlot?.destroy()
      this.piePlot.off('element:click')
    }
  },
}
</script>

<style scoped lang="less"></style>
